import { Applicant, ApplicantAddressType, ApplicationProps } from '../redux-store/types/api-types';
import {
  applicantStatusMasters,
  APPLICATION_LISTING_STATUS,
  APPLICATION_TYPE,
  DLCLMasters,
  CurrencyMaster,
  defaultCurrency,
  KBOLT_XALT_MASTER,
  SALT,
  SALT2,
  USER_ROLES,
  YesNoMaster,
  dlclIdFieldValidationDate,
  FEE_TYPE_MASTER,
  SAVE_LATER_VALIDATION_CHECK,
} from './constant';
import { ToWords } from 'to-words';
import { numberRegex } from './regex';
import _ from 'lodash';
import AdapterMoment from '@mui/lab/AdapterMoment';
import momentTimezone from 'moment-timezone';
import en from '../lang/en-us';
import {
  MenuItemListLink,
  NonIndividualMenuItemListLink,
} from '../components/investors/components';

export const maskMobileNumber = (mobileNumber: string): string => {
  if (!mobileNumber) {
    return '';
  }
  return (
    mobileNumber.slice(0, 2) +
    mobileNumber.slice(2, mobileNumber.length - 2).replace(/\d/g, '*') +
    mobileNumber.slice(mobileNumber.length - 2)
  );
};

export function getOccupationType(occupation?: string | null): boolean {
  return [
    'PVT.SECTOR SERVICE',
    'PUBLIC SECTOR',
    'GOVT.SERVICE',
    'HOUSEWIFE',
    'DEFENCE',
    'PROFESSIONAL',
    'RETIRED',
    'BUSINESS',
    'AGRICULTURE',
    'STUDENT',
    'FOREX DEALER',
    '',
  ].includes(occupation || '');
}

export const getApplicantName = (index: number, isNomineeOrBank?: boolean): string => {
  if (index === 1) {
    return isNomineeOrBank ? 'First' : 'First/Sole';
  } else if (index === 2) {
    return 'Second';
  } else {
    return 'Third';
  }
};

export const minDateForContributor = (): Date => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 18);
  return date;
};
export const maxNetworthDate = (): Date => {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 1);
  return date;
};

export const getFirstHolderName = (applicants: Partial<Applicant>[]): string => {
  return (
    applicants.find((applicant) => ['firstapplicant', '1'].includes(applicant.applicant_type || ''))
      ?.name || 'N/A'
  );
};

export const getApplicantType = (applicantType: string | undefined): string => {
  if (applicantType) {
    if (['firstapplicant', '1'].includes(applicantType)) {
      return '1';
    } else if (['secondapplicant', '2'].includes(applicantType)) {
      return '2';
    } else {
      return '3';
    }
  }
  return '';
};

export const formatToIndianCurrency = (value: number | null): string => {
  const options = { style: 'currency', currency: 'INR' };
  if (value) {
    return new Intl.NumberFormat('en-IN', options).format(value);
  }
  return '';
};

export function toExactSubstr(str: string): string {
  const a = str.split('.');
  if (a.length == 2) {
    return a[0].concat('.', a[1].substr(0, 2));
  }
  return str;
}

export const formatCurrency = (value?: number | null, currencyType?: string): string => {
  let amount: any = Number(value);
  if (currencyType === 'USD') {
    if (amount >= 1000000000) {
      return `${toExactSubstr(`${amount / 1000000000}`)} B`;
    }
    if (amount >= 1000000) {
      return `${toExactSubstr(`${amount / 1000000}`)} M`;
    }
    if (amount >= 1000) {
      return `${toExactSubstr(`${amount / 1000}`)} K`;
    }
    return `${amount}`;
  }
  const formats = ['K', 'L', 'Cr'];
  formats.some((key, i) => {
    const size = Math.pow(10, i === 0 ? 3 : 3 - 1);
    if (size <= amount) {
      amount = toExactSubstr(`${amount / size}`);
      if (amount >= 100 && i < formats.length - 1) {
        i++;
      } else {
        amount += ' ' + formats[i];
        return true;
      }
    } else {
      return true;
    }
  });
  return amount;
};

export const EIGHTEEN_YEARS_IN_MILLISECONDS = 18 * 31556926 * 1000; //  18 * no of seconds in a Year * 1000

export const isMinor = (dateOfBirth = ''): boolean => {
  return new Date().getTime() - new Date(dateOfBirth).getTime() < EIGHTEEN_YEARS_IN_MILLISECONDS;
};

export const getAddressFields = (addressType: string): ApplicantAddressType => {
  return {
    address1: '',
    address2: '',
    // address3: '',
    city: '',
    state: '',
    country: addressType === 'overseas' ? '' : 'INDIA',
    pincode: '',
    permanentAddressSameAsCorresponding: false,
    address_type: addressType,
    isActive: true,
    fetchedFromKRA: null,
  };
};

export const getAddressData = (
  addressType: string,
  addresses: Partial<ApplicantAddressType>[] | undefined
): Partial<ApplicantAddressType> => {
  return (
    addresses?.find((address) => address.address_type === addressType) ||
    getAddressFields(addressType)
  );
};

export function getRelation(relationValue?: string | null): boolean {
  return ['Mother', 'Father', 'Daughter', 'Son', 'Spouse', 'Brother', 'Sister', ''].includes(
    relationValue || ''
  );
}

export function getSourceOfFund(sourceValue?: string | null): boolean {
  return [
    'employment_salary',
    'Business_consultancy_income',
    'dividend_interest',
    'donation_grant',
    'pension_retirement_funds',
    'shareholders_funds',
    'reserves_retained_earnings',
    'sales_of_assets',
    'sales_of_shares_securities_investment',
    'gifts',
    'partners_members_funds',
    'inheritance',
  ].includes(sourceValue || '');
}

export const saveForLater = (
  role: string,
  id?: string,
  applicant1ReferenceId?: string | null
): string => {
  return role === USER_ROLES.INVESTOR
    ? `/investment-details/${applicant1ReferenceId}/application-details`
    : role === USER_ROLES.POAAPPROVER || role === USER_ROLES.AMCAPPROVER
    ? `/application-details/${id}`
    : '/applications';
};

export const maxAge = (dateOfBirth = ''): boolean => {
  const thisYear = new Date().getFullYear();
  return thisYear - new Date(dateOfBirth).getFullYear() > 125;
};
export const futureAge = (dateOfBirth = ''): boolean => {
  return new Date(dateOfBirth) > new Date();
};
export function currencyConversion(
  num?: string | number | null,
  currencyType?: string | null
): string | null {
  if (!num) return '';
  const toWords = new ToWords({
    localeCode: CurrencyMaster[currencyType?.toLowerCase() || defaultCurrency].localeCode,
  });
  const words = toWords.convert(Number(num), { currency: true });
  return words;
}

export function checkIfApplicationIsNonIndividual({ applicationType }: ApplicationProps): boolean {
  return applicationType === APPLICATION_TYPE.NON_INDIVIDUAL;
}

// eslint-disable-next-line @typescript-eslint/no-var-requires
const countryCodes = require('country-codes-list');
export const getCountryCodes = (): { label: string; key: string; countryCode: string }[] => {
  const countryCodesObject: { [key: string]: string } = countryCodes.customList(
    'countryCode',
    '{countryNameEn}: +{countryCallingCode}'
  );

  const modifiedCodes = Object.keys(countryCodesObject).map((cc) => ({
    label: countryCodesObject[cc],
    key: countryCodesObject[cc].split(':')[1].trim(),
    countryCode: cc,
  }));
  return modifiedCodes;
};

// eslint-disable-next-line
export const preventSpecialCharacters = (e: any) => {
  if (
    !((e.ctrlKey || e.metaKey) && e.keyCode == 67) &&
    !((e.ctrlKey || e.metaKey) && e.keyCode == 86) &&
    !((e.ctrlKey || e.metaKey) && e.keyCode == 88) &&
    !((e.ctrlKey || e.metaKey) && e.keyCode == 90) &&
    ![
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Tab',
      'Enter',
    ].includes(e.key) &&
    !numberRegex.test(e.key)
  ) {
    e.preventDefault();
  }
};
export function allowOnlyNumbers(e: any): void {
  if (['-', '+', 'e', 'E'].includes(e.key)) {
    e.preventDefault();
  }
}
export const permanentAddressSameAsCorresponding = (
  updatedCorrespondingAddress: Partial<ApplicantAddressType>,
  addressType: string,
  addresses: Partial<ApplicantAddressType>[] | undefined
): Partial<ApplicantAddressType> => {
  // eslint-disable-next-line
  const { address1, address2, city, pincode, state, country, ...rest } =
    updatedCorrespondingAddress;
  return {
    ...getAddressData(addressType, addresses),
    address1: address1,
    address2: address2,
    city: city,
    pincode: pincode,
    state: state,
    country: country,
    address_type: addressType,
  };
};
export const _updatedAddresses = (
  updatedAddresses: {
    nationality?: string | null | undefined;
    status?: string | null | undefined;
    permanent?: Partial<ApplicantAddressType> | undefined;
    correspondence?: Partial<ApplicantAddressType> | undefined;
    overseas?: Partial<ApplicantAddressType> | undefined;
    [key: string]: string | Partial<ApplicantAddressType> | null | undefined;
  },
  applicant: Partial<Applicant>
): Partial<ApplicantAddressType>[] => {
  const { nationality, status, ...addressProps } = updatedAddresses;
  return Object.keys(addressProps)
    .map((addressType) => {
      // if (applicant.nationality?.toLowerCase() === 'indian' && addressType === 'overseas') {
      //   return {};
      // }
      if (
        addressType === 'permanent' &&
        addressProps['overseas']?.permanentAddressSameAsCorresponding
      ) {
        return permanentAddressSameAsCorresponding(
          addressProps['overseas'],
          addressType,
          applicant.addresses
        );
      }
      if (
        addressType === 'permanent' &&
        addressProps['correspondence']?.permanentAddressSameAsCorresponding &&
        !addressProps['overseas']
      ) {
        return permanentAddressSameAsCorresponding(
          addressProps['correspondence'],
          addressType,
          applicant.addresses
        );
      }
      if (
        Object.keys(addressProps).length === 1 &&
        addressProps['overseas']?.permanentAddressSameAsCorresponding
      ) {
        return [
          {
            ...permanentAddressSameAsCorresponding(
              addressProps['overseas'],
              'permanent',
              applicant.addresses
            ),
          },
          {
            ...getAddressData(addressType, applicant.addresses),
            ...(addressProps[addressType] as unknown as Partial<ApplicantAddressType>),
            address_type: addressType,
          },
        ];
      }
      if (
        Object.keys(addressProps).length === 1 &&
        addressProps['correspondence']?.permanentAddressSameAsCorresponding
      ) {
        return [
          {
            ...permanentAddressSameAsCorresponding(
              addressProps['correspondence'],
              'permanent',
              applicant.addresses
            ),
          },
          {
            ...getAddressData(addressType, applicant.addresses),
            ...(addressProps[addressType] as unknown as Partial<ApplicantAddressType>),
            address_type: addressType,
          },
        ];
      }
      return {
        ...getAddressData(addressType, applicant.addresses),
        ...(addressProps[addressType] as unknown as Partial<ApplicantAddressType>),
        address_type: addressType,
      };
    })
    .flat()
    .filter((address) => Object.keys(address).length);
};
export const applyRoleBasedStatus = (role: any): boolean => {
  return [
    USER_ROLES.RM,
    USER_ROLES.SUBDISTRIBUTOR,
    USER_ROLES.DISTRIBUTOR,
    USER_ROLES.AMCAPPROVER,
  ].includes(role);
};

export const checkKraBasedOnStatus = (status: any): boolean => {
  return ![
    APPLICATION_LISTING_STATUS.invitationexpired,
    APPLICATION_LISTING_STATUS.signed,
    APPLICATION_LISTING_STATUS.completed,
    APPLICATION_LISTING_STATUS.rejected,
  ].includes(APPLICATION_LISTING_STATUS[status]);
};

export const applicationComparison = (
  existingApplication: Partial<ApplicationProps> | null,
  updatedApplication: Partial<ApplicationProps>
): boolean => {
  return _.isEqual(existingApplication, updatedApplication);
};

export const sendApplication_Nri = (application?: ApplicationProps | null): boolean | undefined => {
  return application?.applicants.map((applicant) => applicant.status).includes('NRI');
};

export const checkForCorrespondenceAddress = (
  nationality: string | null | undefined,
  status: string | null | undefined
): boolean => {
  return (
    nationality?.toLowerCase() === 'indian' &&
    applicantStatusMasters[status as string] === applicantStatusMasters.Individual
  );
};

export const setUpFeeCalculations = (
  commitmentAmount: number,
  setupFeePercentage: number
): { feeAmount: number; feeGst: number; totalSetupFee: number } => {
  const setupFeeAmount = (Number(commitmentAmount) * Number(setupFeePercentage)) / 100;
  const GstForSetupFee = (setupFeeAmount * 18) / 100;
  return {
    feeAmount: Number(setupFeeAmount),
    feeGst: Number(GstForSetupFee),
    totalSetupFee: Number(setupFeeAmount + GstForSetupFee),
  };
};

export const checkAddressField = (data?: string) => {
  if (data) {
    if (data.includes(' ,')) {
      return data.split(' ,').join(',') + ' ';
    }
    return data.includes(',') ? data + ' ' : data + ', ';
  }
  return '';
};

export const getBankAddress = (
  address1: string | undefined,
  address2: string | undefined,
  address3: string | undefined
): string => {
  return `${address2 ? checkAddressField(address1) || '' : address1 || '' || ''}${
    address3 ? checkAddressField(address2) || '' : address2 || ''
  }${address3 || ''}`;
};

export const checkSupportLogin = (createdBy: string | number, id: string | number) => {
  return Number(createdBy) === Number(id);
};

export const checkSubDisDisable = (role: any, createdBy: string | number, id: string | number) => {
  return (
    [USER_ROLES.DISTRIBUTOR, USER_ROLES.INVESTOR, USER_ROLES.POAAPPROVER].includes(role) ||
    (USER_ROLES.SUBDISTRIBUTOR === role && !checkSupportLogin(Number(createdBy), Number(id)))
  );
};

export const numToAlpMonth = (numMonth: any, month: any) => {
  numMonth?.map((monthNum: any) => {
    if (monthNum === 1) {
      month.push('Jan');
    } else if (monthNum === 2) {
      month.push('Feb');
    } else if (monthNum === 3) {
      month.push('Mar');
    } else if (monthNum === 4) {
      month.push('Apr');
    } else if (monthNum === 5) {
      month.push('May');
    } else if (monthNum === 6) {
      month.push('Jun');
    } else if (monthNum === 7) {
      month.push('Jul');
    } else if (monthNum === 8) {
      month.push('Aug');
    } else if (monthNum === 9) {
      month.push('Sep');
    } else if (monthNum === 10) {
      month.push('Oct');
    } else if (monthNum === 11) {
      month.push('Nov');
    } else if (monthNum === 12) {
      month.push('Dec');
    }
  });
};

export const removeSingleQuote = (value: string | null | undefined): any => {
  if (value && value.includes("'")) return value.split("'").join('');
  else return value;
};

export function removeEqualOperator(value = ''): string {
  const lastTwoIndex = value.length - 2;
  let encodeString = value.slice(0, lastTwoIndex);
  const slicedStr = value.slice(lastTwoIndex, value.length);
  slicedStr.split('').forEach((char) => {
    if (char !== '=') {
      encodeString += char;
    }
  });
  return encodeString;
}

export function encodeBase64(value: string): string {
  const encodeValue = btoa(value);
  const encodeSubString = removeEqualOperator(encodeValue);
  const encodePayload = btoa(`${SALT}${encodeSubString}${SALT2}`);
  return encodePayload;
}

export function getNomineeRelation(relationValue?: string | null): boolean {
  return ['MOTHER', 'FATHER', 'DAUGHTER', 'SON', 'SPOUSE', 'BROTHER', 'SISTER', ''].includes(
    relationValue || ''
  );
}

// Date Dependent Validation for fields that were made mandatory post live
export const checkValidationBasedOnDate = (
  date: string | number | Date | undefined,
  validationStartDate: string
): boolean => {
  if (!date) return false;
  const dateForCompare = date ? new Date(date) : new Date();
  const dateForApplyValidations = new Date(validationStartDate);
  return dateForCompare.getTime() >= dateForApplyValidations.getTime();
};

export const getdpIdField = (value: string): boolean => {
  return (
    DLCLMasters[value || ''] === DLCLMasters.cdsl || DLCLMasters[value || ''] === DLCLMasters.nsdl
  );
};

export function checkPmsPoa(applicants: Partial<Applicant>[]): boolean {
  return applicants
    .map((applicant) => YesNoMaster[applicant.POADP || ''] !== YesNoMaster.yes)
    .every((item) => item);
}

export const isCDSL = (value: string): boolean => {
  return (
    DLCLMasters[(value as string) || ''] === DLCLMasters.none ||
    DLCLMasters[(value as string) || ''] === DLCLMasters.cdsl
  );
};

export const cleardpIdField = (value: string): boolean => {
  return DLCLMasters[(value as string) || ''] === DLCLMasters.none;
};

export const getclIdField = (value: string): boolean => {
  return DLCLMasters[value || ''] === DLCLMasters.nsdl;
};

export const booleanToYesNo = (value?: boolean): string => (value ? 'Yes' : 'No');

export function applicationDownloadStatusCheck(status: string): boolean {
  return APPLICATION_LISTING_STATUS[status] !== APPLICATION_LISTING_STATUS.completed;
}
export const isApplicableSetupfee = (setupFeeApplicable: string) => {
  return YesNoMaster[setupFeeApplicable] === YesNoMaster.yes;
};

export const isDateSame = (date1: Date, date2: Date): boolean => {
  const timeZoneFromServer = 'Asia/Kolkata';
  const { moment } = new AdapterMoment({ instance: momentTimezone });
  moment.tz.setDefault(timeZoneFromServer);
  momentTimezone.tz.setDefault(timeZoneFromServer);
  return (
    moment(date1).get('D') === moment(date2).get('D') &&
    moment(date1).get('M') === moment(date2).get('M') &&
    moment(date1).get('y') === moment(date2).get('y')
  );
};

export const calculateContributionAmt = (
  commitmentAmt: number,
  minContributionPercentage: number
): number => {
  return (Number(commitmentAmt) * Number(minContributionPercentage)) / 100;
};

export const dlclIdFieldMandatory = (): boolean => {
  return checkValidationBasedOnDate(new Date(), dlclIdFieldValidationDate);
};

export function isAccreditedInvestorApplicable(accreditedInvestorApplicable: string): boolean {
  return accreditedInvestorApplicable?.toLowerCase() === 'yes';
}

export function isFieldApplicable(value: string): boolean {
  return YesNoMaster[value?.toLowerCase()] === YesNoMaster.yes;
}

export function isFieldVariable(FeeTypeValue: string | null, fieldApplicable: string): boolean {
  return (
    FEE_TYPE_MASTER[FeeTypeValue?.toLowerCase() || ''] === FEE_TYPE_MASTER.variable &&
    YesNoMaster[fieldApplicable?.toLowerCase()] === YesNoMaster.yes
  );
}

/**
 * Replace with your file URL or API endpoint
 * @param fileUrl
 *
 * Desired file name
 * @param fileName
 */
export const handleDownload = async (fileUrl: string, fileName: string): Promise<void> => {
  try {
    const url = new URL(fileUrl, window.location.origin);

    const isSameOrigin = url.origin === window.location.origin;

    if (isSameOrigin) {
      // Same-origin: rename supported
      const response = await fetch(fileUrl);

      if (!response.ok) {
        throw new Error('Unable to download file');
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();

      link.remove();
      URL.revokeObjectURL(blobUrl);
    } else {
      // Cross-origin (e.g. S3): no fetch => no CORS issue
      const link = document.createElement('a');
      link.href = fileUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';

      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  } catch (error) {
    console.error('Download failed:', error);
    throw error;
  }
};

export function getLastFinancialYearEnd() {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0 is January, 11 is December

  // If the current date is before April (currentMonth < 3), the financial year is lastYear-1 to lastYear
  const lastFinancialYear = currentMonth < 3 ? currentYear - 1 : currentYear;

  // Return March 31st of the last financial year
  return new Date(lastFinancialYear, 2, 31); // March is month index 2
}

// console.log(getLastFinancialYearEnd().toDateString());

export const ifPushToXalt = (value: string): boolean => {
  return [KBOLT_XALT_MASTER.XALT, KBOLT_XALT_MASTER.KBOLT_XALT].includes(value);
};

export function getDocNumberForMultipleDocs(docArraylength: number): string | null {
  switch (docArraylength.toString()) {
    case '1':
      return 'first ';
    case '2':
      return 'second ';
    case '3':
      return 'third ';
    case '4':
      return 'forth';
    default:
      return '';
  }
}

export function getLimitForDoc(documentType: string): number {
  switch (documentType) {
    case 'Pan_card_authorised_signatories':
      return 4;
    case 'panOfDirectors':
      return 4;
    default:
      return 2;
  }
}

export const getCurrentStep = (
  applicationType: string,
  current: string,
  isSaveLater: boolean
): number => {
  const navList =
    applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
      ? NonIndividualMenuItemListLink
      : MenuItemListLink;

  const normalizedCurrent = current.split('/').pop();
  const stepIndex = navList.findIndex((item) => item === normalizedCurrent);
  const currentStep = stepIndex + 1;

  return !SAVE_LATER_VALIDATION_CHECK && isSaveLater ? currentStep - 1 : currentStep;
};

export const navigateToNextStep = (
  currentPath: string,
  role: string,
  id: string,
  isSaveLater = false,
  applicant1ReferenceId: string,
  applicationType: string
): string => {
  const navList =
    applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
      ? NonIndividualMenuItemListLink
      : MenuItemListLink;
  const normalizedCurrent = currentPath.split('/').pop();
  const currentIndex = navList.findIndex((path) => path === normalizedCurrent);
  const nextIndex = currentIndex + 1;
  if (nextIndex < navList.length && !isSaveLater) {
    return navList[nextIndex];
  }
  // Below Logic is for last page navigation
  // Handle INVESTOR role
  if (role === USER_ROLES.INVESTOR) {
    return `/investment-details/${applicant1ReferenceId}/application-details`;
  }

  // Handle POA/AMC Approver role
  if (role === USER_ROLES.POAAPPROVER || role === USER_ROLES.AMCAPPROVER) {
    return `/application-details/${id}`;
  }

  // Handle Save Later path
  if (isSaveLater) {
    return `/applications`;
  }

  // Handle based on application type
  return applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
    ? `/non-individual-application-preview/${id}`
    : `/application-preview/${id}`;
};

export function formatToIndianShort(num: number): string {
  if (num == null || isNaN(num)) return '';

  num = Number(num);

  if (num >= 1_00_00_000) {
    // Crore
    return (num / 1_00_00_000).toFixed(num % 1_00_00_000 === 0 ? 0 : 2) + ' Cr';
  } else if (num >= 1_00_000) {
    // Lakh
    return (num / 1_00_000).toFixed(num % 1_00_000 === 0 ? 0 : 2) + ' Lakh';
  } else if (num >= 1000) {
    // Thousand
    return (num / 1000).toFixed(num % 1000 === 0 ? 0 : 2) + ' K';
  } else {
    return num.toString(); // 0–999
  }
}
export const getArrayLength = (arrayList: number): number[] => {
  return Array.from(
    {
      length: arrayList,
    },
    (value, index) => index + 1
  );
};

export const minNetworthDate = (value: string): Date => {
  const date = new Date(value);
  date.setFullYear(date.getFullYear() - 1);
  return date;
};

export const ICEF_I_Fund = (id?: number): boolean => {
  return id === 3;
};

// export const formatCurrencyAmt = (price: number, currency: string): string => {
//   const formatter = new Intl.NumberFormat(CurrencyMaster[currency || defaultCurrency]?.localeCode, {
//     style: 'currency',
//     currency: CurrencyMaster[currency || defaultCurrency]?.currency,
//     // minimumFractionDigits: 2, // Ensure two decimal places
//     // maximumFractionDigits: 2,
//   });

//   const formattedPrice = formatter.format(price);
//   return formattedPrice;
// };

export const formatCurrencyAmt = (price: number, currency: string): string => {
  const formatter = new Intl.NumberFormat(CurrencyMaster[currency || defaultCurrency]?.localeCode);

  const formattedPrice = formatter.format(price);
  return formattedPrice;
};

export const isUSstate = (nation: string): boolean => {
  return nation?.toUpperCase() === 'UNITED STATES';
};

export const isIndia_Assets_Excellence_Fund1 = (schemeId: number | null): boolean => {
  return schemeId === 4;
};

// Triggers a browser download for a Blob returned from the API middleware.
export const downloadBlobFile = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
